<template>
  <div class="notification-wrapper">
    <el-popover
      width="350"
      trigger="manual"
      v-model="visible"
      title="未读通知"
      @hide="onHide"
      popper-class="notification-popper"
    >
      <template #reference>
        <div class="notification-trigger" @click="toggleNotificationList">
          <el-badge :value="Math.max(unreadNotificationsCount, 0)" :hidden="!unreadNotificationsCount" :max="99">
            <i class="el-icon-bell"></i>
          </el-badge>
        </div>
      </template>

      <div class="notification-container" v-loading="fetchNotificationRequestLoading">
        <div class="notification-top-header" v-if="notificationPagination.total > 0 && abilityService.can('notifications.update')">
          <el-button type="primary" size="mini" icon="el-icon-check" @click="markAllAsRead">全部已读</el-button>
        </div>

        <div class="notification-list" v-if="notifications.length">
          <div
            v-for="item in notifications"
            :key="item.id"
            class="notification-item"
          >
            <div class="notification-content">
              <div class="notification-header">
                <el-tag size="mini" type="primary">
                  {{ $t(`notification.kinds.${item.kind}`) }}
                </el-tag>
                <span class="notification-time">{{ item.created_at | formatTime }}</span>
              </div>
              <div class="notification-desc">
                <NotificationContent :notification="item" />
              </div>
              <div class="notification-footer" v-if="abilityService.can('notifications.update')">
                <el-button
                  type="primary"
                  size="small"
                  @click="markAsRead(item.id)"
                  :disabled="item.is_read"
                  :loading="readLoading[item.id]"
                >
                  <i class="el-icon-check"></i> 已读
                </el-button>
              </div>
            </div>
          </div>

          <div class="notification-more" v-if="notificationPagination.total > notifications.length">
            <router-link :to="{ name: 'notifications.index' }">
              <el-button type="primary" size="small">查看全部</el-button>
            </router-link>
          </div>
        </div>

        <div v-else class="notification-empty">
          暂无通知
        </div>
      </div>
    </el-popover>
  </div>
</template>

<script>
import { Vue, Component, Watch } from 'vue-property-decorator';
import { polling } from '@/utils/polling';
import moment from 'moment';
import NotificationContent from '@/components/notification-content.vue';
import { NOTIFICATION_PUSHED_STORAGE_KEY } from '@/constants';
import notificationSound from '@/assets/notification.mp3?url';
import { abilityService } from 'beans-admin-plugin';

@Component({
  components: {
    NotificationContent
  },
  filters: {
    formatTime(time) {
      return moment(time).format('YYYY-MM-DD HH:mm:ss');
    }
  }
})
export default class Notification extends Vue {
  cancelPolling = null;
  visible = false;
  fetchNotificationRequest = null;
  readLoading = {};
  fetchNotificationRequestLoading = false;
  notifications = [];
  notificationPagination = {};
  unreadNotificationsCount = 0;
  abilityService = abilityService;

  @Watch('$route.fullPath')
  onRouteChange() {
    this.visible = false;
  }

  cancelFetchMotificationRequest() {
    if (this.fetchNotificationRequest && this.fetchNotificationRequest.engine) {
      this.fetchNotificationRequest.engine.abort();
    }
  }

  onHide() {
    this.cancelFetchMotificationRequest();
  }

  closeNotify() {
    if (this.notify) {
      this.notify.close();
    }
  }

  async fetchNotification() {
    this.fetchNotificationRequestLoading = true;
    try {
      this.fetchNotificationRequest = this.$request.get('/notifications', { read_at_null: true, per_page: 50 }, { $alert: false, loading: false });
      const { data, pagination } = await this.fetchNotificationRequest;
      this.notifications = data;
      this.notificationPagination = pagination;
      this.unreadNotificationsCount = pagination.total;
    } finally {
      this.fetchNotificationRequestLoading = false;
    }
  }

  async showNotification(notification) {
    const storageKey = `${NOTIFICATION_PUSHED_STORAGE_KEY}_user_${this.$root.user.id}`;
    // 旧消息不再重复通知
    const latestNotificationId = Number(localStorage.getItem(storageKey)) || 0;
    if (latestNotificationId >= notification.id) {
      return;
    }
    const audio = new Audio(notificationSound);
    const NotificationContentExtend = Vue.extend(NotificationContent);

    const notificationContentInstance = new NotificationContentExtend({
      i18n: this.$i18n,
      router: this.$router,
      propsData: {
        notification
      }
    }).$mount();
    audio.play();
    this.notify = this.$notify({
      type: 'info',
      customClass: 'custom-notification',
      title: this.$t(`notification.kinds.${notification.kind}`),
      message: notificationContentInstance.$el.innerHTML,
      dangerouslyUseHTMLString: true,
      duration: 0,
      onClose: () => {
        this.markAsRead(notification.id, { $alert: false, $loading: false });
        notificationContentInstance.$destroy();
        notificationContentInstance.$el.remove();
      }
    });
    localStorage.setItem(storageKey, notification.id);
  }

  async fetchNews() {
    const data = await this.$request.get('mine/news', {}, { $alert: false, loading: false });
    this.unreadNotificationsCount = data.unread_notifications_count;
    if (this.unreadNotificationsCount > 0) {
      const notifications = await this.$request.get('notifications', { read_at_null: true, per_page: 1, page: 1 }, { $alert: false, loading: false });
      const notification = notifications[0];
      if (notification) {
        this.showNotification(notification);
      }
    }
  }

  toggleNotificationList() {
    this.visible = !this.visible;
    if (this.visible) {
      this.fetchNotification();
    }
  }

  async markAllAsRead() {
    await this.$autoLoading(this.$request.put('/notifications/read_all'));
    this.visible = false;
    this.unreadNotificationsCount = 0;
  }

  async markAsRead(id, requestOptions = {}) {
    if (!abilityService.can('notifications.update')) {
      return;
    }

    this.$set(this.readLoading, id, true);
    try {
      await this.$request.put(`/notifications/${id}/read`, {}, requestOptions);
      this.unreadNotificationsCount--;
      const index = this.notifications.findIndex(item => item.id === id);
      if (index !== -1) {
        this.notifications[index].is_read = true;
      }
    } finally {
      this.$set(this.readLoading, id, false);
    }
  }

  clickAbortController = new AbortController();
  mounted() {
    // 开发环境不请求这个
    if (process.env.VUE_APP_ENV !== 'development') {
      this.fetchNews();
      this.cancelPolling = polling(this.fetchNews, 60 * 1000);
    }

    document.addEventListener('click', (event) => {
      const popover = document.querySelector('.notification-popper');
      const trigger = document.querySelector('.notification-trigger');
      if (this.visible && popover) {
        // 点击位置既不在弹窗内，也不在触发按钮上时，关闭弹窗
        if (!popover.contains(event.target) && !trigger.contains(event.target)) {
          this.visible = false;
        }
      }
    }, {
      signal: this.clickAbortController.signal
    });

  }

  beforeDestroy() {
    this.cancelPolling && this.cancelPolling();
    this.cancelFetchMotificationRequest();
    this.closeNotify();
    this.clickAbortController.abort();
  }
}
</script>

<style lang="scss">
.custom-notification.el-notification {
  .el-notification__icon {
    color: #f56c6c;
  }
}
</style>

<style lang="scss" scoped>
.notification-wrapper {
  position: relative;
  display: inline-block;
}

.notification-trigger {
  cursor: pointer;
  padding: 0 8px;

  i {
    font-size: 20px;
    color: #333;
  }

  :deep(.el-badge__content) {
    background-color: #ff4d4f;
    border: none;
  }
}

.notification-container {
  .notification-top-header {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
  }

  .notification-list {
    max-height: 400px;
    overflow-y: auto;
  }

  .notification-item {
    padding: 12px 16px;
    border: none;
    margin-bottom: 8px;
    border-radius: 8px;
    transition: all 0.2s ease;
    background-color: #f8f9fc;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    &:hover {
      background-color: #f0f2f7;
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    }
  }

  .notification-content {
    .notification-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
    }

    .notification-desc {
      color: #1f2329;
      font-size: 14px;
      line-height: 1.5;
      width: 100%;
      margin-top: 12px;
      margin-bottom: 12px;
    }

    .notification-time {
      color: #8f959e;
      font-size: 12px;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .notification-footer {
      display: flex;
      justify-content: flex-end;
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid rgba(0, 0, 0, 0.06);

      .el-button {
        padding: 6px 12px;
        border-radius: 6px;
      }
    }
  }

  .notification-empty {
    padding: 32px 0;
    text-align: center;
    color: #999;
  }

  .notification-more {
    text-align: center;
    margin: 10px 0;
  }
}
</style>
